// src/hooks/useDensity.js

import { useCallback, useEffect, useState } from "react";

/**
 * Display density for the incident console.
 *
 * Experts work incidents for an hour at a time, often on a laptop with a shared
 * screen taking half the display. Compact mode trades whitespace for rows of
 * timeline and findings visible at once. The preference is per-device rather
 * than per-account: it is a property of the screen you are sitting at.
 */

export const DENSITY = {
  COMFORTABLE: "comfortable",
  COMPACT: "compact",
};

const STORAGE_KEY = "skillsyncs.workspace.density";

function readStored() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === DENSITY.COMPACT ? DENSITY.COMPACT : DENSITY.COMFORTABLE;
  } catch {
    // Private browsing and blocked storage both throw here; the default is fine.
    return DENSITY.COMFORTABLE;
  }
}

export function useDensity() {
  const [density, setDensity] = useState(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, density);
    } catch {
      // Preference is cosmetic; failing to persist it is not worth surfacing.
    }
  }, [density]);

  const toggleDensity = useCallback(() => {
    setDensity((current) =>
      current === DENSITY.COMPACT ? DENSITY.COMFORTABLE : DENSITY.COMPACT
    );
  }, []);

  return { density, toggleDensity, isCompact: density === DENSITY.COMPACT };
}

export default useDensity;
